import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import usePageMetadata from '../utils/usePageMetadata';
import './EditProfile.css';
import '../components/DeleteConfirmationModal.css';

const EditProfile = () => {
  usePageMetadata({
    title: 'Edit Profile | The Ad Shop',
    description: 'Update your account information, email, password, and preferences.'
  });

  const { token, login, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    websiteUrl: '',
    newsletterSubscribed: false
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInput, setDeleteInput] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get('/api/auth/profile');
      // Handle newsletterSubscribed - convert to boolean, handle string "true"/"false", null, undefined
      const newsletterValue = response.data.user.newsletterSubscribed;
      const newsletterSubscribed = newsletterValue === true || newsletterValue === 'true' || newsletterValue === 1;
      console.log('Newsletter subscription status:', {
        raw: newsletterValue,
        converted: newsletterSubscribed,
        type: typeof newsletterValue
      });
      setFormData({
        firstName: response.data.user.firstName,
        lastName: response.data.user.lastName,
        email: response.data.user.email,
        websiteUrl: response.data.user.websiteUrl,
        newsletterSubscribed: newsletterSubscribed
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleNewsletterChange = (e) => {
    setFormData({
      ...formData,
      newsletterSubscribed: e.target.checked
    });
  };

  const openDeleteModal = () => {
    setDeleteInput('');
    setDeleteError('');
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false);
  };

  const handleAccountDeletion = async () => {
    if (deleteInput.trim().toLowerCase() !== 'delete') {
      setDeleteError('Please type DELETE to confirm.');
      return;
    }

    setDeleteLoading(true);
    setDeleteError('');

    try {
      await axios.delete('/api/auth/delete-account', {
        data: { confirm: 'delete' }
      });

      logout();
      setShowDeleteModal(false);
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete account. Please try again.';
      setDeleteError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    setSuccess('');

    try {
      const updateData = { ...formData };
      
      // Only include password fields if new password is provided
      if (passwordData.newPassword) {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
          setErrors({ ...errors, confirmPassword: 'Passwords do not match' });
          setLoading(false);
          return;
        }
        updateData.currentPassword = passwordData.currentPassword;
        updateData.newPassword = passwordData.newPassword;
      }

      const response = await axios.put('/api/auth/profile', updateData);
      setSuccess('Profile updated successfully!');
      
      // Update auth context with new user data
      login(response.data.user, token);
      
      // Clear password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => {
        navigate('/dashboard'); // Dashboard is now the analytics page
      }, 1500);
    } catch (error) {
      if (error.response?.data?.message) {
        setErrors({ general: error.response.data.message });
      } else {
        setErrors({ general: 'Failed to update profile. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  if (loadingProfile) {
    return (
      <div className="page-container">
        <div className="profile-container">
          <div className="loading">Loading profile...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="profile-container">
        <div className="profile-card">
          <div className="profile-header">
            <h1>Edit Profile</h1>
            <p>Update your account information</p>
          </div>

          {errors.general && (
            <div className="error-message">{errors.general}</div>
          )}

          {success && (
            <div className="success-message">{success}</div>
          )}

          <form onSubmit={handleSubmit} className="profile-form">
            <div className="form-section">
              <h2>Personal Information</h2>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="websiteUrl">Website URL</label>
                <input
                  type="url"
                  id="websiteUrl"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-section">
              <h2>Newsletter Subscription</h2>
              
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="newsletterSubscribed"
                    checked={Boolean(formData.newsletterSubscribed)}
                    onChange={handleNewsletterChange}
                  />
                  <span>Subscribe to our newsletter for publisher-facing ad industry news and revenue optimization tips</span>
                </label>
              </div>
            </div>

            <div className="form-section">
              <h2>Change Password (Optional)</h2>
              <p className="section-description">Leave blank if you don't want to change your password</p>
              
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  placeholder="Enter current password"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    placeholder="Confirm new password"
                  />
                  {errors.confirmPassword && <span className="field-error">{errors.confirmPassword}</span>}
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button type="button" onClick={() => navigate('/dashboard')} className="btn btn-secondary">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>

          <div className="form-section danger-zone">
            <h2>Delete Account</h2>
            <p className="danger-description">
              Permanently remove your The Ad Shop account, all connected Google data, and any analytics or compliance records. This action cannot be undone.
            </p>
            <button type="button" className="btn btn-danger" onClick={openDeleteModal}>
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-content">
            <div className="delete-modal-header">
              <div className="delete-modal-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2>Delete Account</h2>
            </div>

            <div className="delete-modal-body">
              <p className="delete-modal-warning">
                This will permanently delete your account and all associated data.
              </p>

              <div className="delete-modal-danger">
                <p>
                  <strong>This action cannot be undone.</strong> Deleting your account will:
                </p>
                <ul>
                  <li>Remove all analytics, insights, and compliance records</li>
                  <li>Revoke access to your connected Google services</li>
                  <li>Delete your sites, settings, and saved preferences</li>
                  <li>Log you out of The Ad Shop immediately</li>
                </ul>
              </div>

              <div className="delete-modal-confirm">
                <label htmlFor="confirmDelete">
                  Type <strong>DELETE</strong> to confirm.
                </label>
                <input
                  id="confirmDelete"
                  type="text"
                  value={deleteInput}
                  onChange={(e) => setDeleteInput(e.target.value)}
                  placeholder="DELETE"
                  disabled={deleteLoading}
                />
                {deleteError && <div className="modal-error">{deleteError}</div>}
              </div>
            </div>

            <div className="delete-modal-actions">
              <button className="btn-cancel" onClick={closeDeleteModal} disabled={deleteLoading}>
                Cancel
              </button>
              <button className="btn-delete" onClick={handleAccountDeletion} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditProfile;

